import React from 'react';
import { Grid } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell
} from 'recharts';
import { formatPercent } from '../../utils/dateFormatters';

const { useBreakpoint } = Grid;

/**
 * ProviderRankingChart - Bar chart ranking providers by inventory health (Responsive)
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {boolean} props.groupByNit - Whether data is grouped by NIT
 */
function ProviderRankingChart({ data, groupByNit = false }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;
  const isMd = screens.md;

  // Transform data for chart
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    if (!groupByNit) {
      return [];
    }

    // Aggregate by NIT
    const byNit = {};
    data.forEach(record => {
      const nit = record.Nit || 'Todos';
      if (!byNit[nit]) {
        byNit[nit] = {
          Nit: nit,
          VerdeCount: 0,
          AmarilloCount: 0,
          TotalReferences: 0
        };
      }
      byNit[nit].VerdeCount += record.VerdeCount || 0;
      byNit[nit].AmarilloCount += record.AmarilloCount || 0;
      byNit[nit].TotalReferences += record.TotalReferences || 0;
    });

    // Calculate health percentage and sort
    return Object.values(byNit)
      .map(item => ({
        ...item,
        HealthPercent: item.TotalReferences > 0
          ? ((item.VerdeCount + item.AmarilloCount) / item.TotalReferences) * 100
          : 0
      }))
      .filter(item => item.Nit !== 'Todos')
      .sort((a, b) => b.HealthPercent - a.HealthPercent)
      .slice(0, 10); // Top 10 providers
  }, [data, groupByNit]);

  // Responsive settings
  const chartHeight = isXs ? 250 : isSm ? 300 : 350;
  const marginValue = isXs ? 8 : 20;
  const yAxisWidth = isXs ? 60 : isMd ? 70 : 80;

  // Get bar color based on health percentage
  const getBarColor = (percent) => {
    if (percent >= 70) return '#52c41a';
    if (percent >= 40) return '#faad14';
    return '#f5222d';
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '12px'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{data.Nit}</p>
        <p style={{ margin: '2px 0', color: '#52c41a' }}>
          Productos saludables: {formatPercent(data.HealthPercent / 100)}
        </p>
        <p style={{ margin: '2px 0', fontSize: '11px', color: '#6b7280' }}>
          (con 30% o mas de inventario)
        </p>
        <p style={{ margin: '2px 0' }}>
          Total productos: {data.TotalReferences}
        </p>
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: chartHeight,
        color: '#9ca3af',
        fontSize: isXs ? 12 : 14,
        textAlign: 'center',
        padding: '0 16px'
      }}>
        {groupByNit
          ? 'No hay datos disponibles por proveedor'
          : 'Activa "Agrupar por Proveedor" en los filtros de arriba para ver este ranking'
        }
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: marginValue, right: 30, left: 10, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} style={{ fontSize: isXs ? 9 : 11 }} />
        <YAxis
          dataKey="Nit"
          type="category"
          width={yAxisWidth}
          tick={{ fontSize: isXs ? 8 : 9 }}
          tickFormatter={(value) => {
            // Truncate long NITs on mobile
            if (isXs && value.length > 12) {
              return value.substring(0, 12) + '...';
            }
            return value;
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar
          dataKey="HealthPercent"
          name="Salud del Inventario"
          radius={[0, 4, 4, 0]}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getBarColor(entry.HealthPercent)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default ProviderRankingChart;
